import type { TenantAiStatus } from '@avrahami/contracts';
import { Badge, type BadgeTone } from '@avrahami/ui';
import { he } from '../i18n/he';
import { budgetState, formatUsdMicros, percentOf, type BudgetState } from '../lib/ai';

const fills = {
  OK: 'bg-brand-600',
  NEAR: 'bg-amber-500',
  REACHED: 'bg-red-600',
} as const satisfies Record<BudgetState, string>;

const badgeTones = {
  OK: 'green',
  NEAR: 'amber',
  REACHED: 'red',
} as const satisfies Record<BudgetState, BadgeTone>;

/**
 * A horizontal bar filled to a percentage. `meter` is a level (a budget in use), `progressbar`
 * is work on its way to done (products with copy).
 */
export function Meter({
  role,
  label,
  percent,
  state = 'OK',
  className = '',
}: {
  role: 'meter' | 'progressbar';
  label: string;
  percent: number;
  state?: BudgetState;
  className?: string;
}) {
  return (
    <div
      role={role}
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      aria-valuetext={`${String(percent)}%`}
      className={`h-2 overflow-hidden rounded-full bg-slate-200 ${className}`}
    >
      <div
        className={`h-full rounded-full ${fills[state]}`}
        style={{ width: `${String(percent)}%` }}
      />
    </div>
  );
}

/** "$12.34 מתוך $50.00", in the reading order of the amounts. */
export function SpentOfLimit({
  spentMicros,
  limitMicros,
}: {
  spentMicros: number;
  limitMicros: number;
}) {
  return (
    <>
      <span className="ltr font-semibold" data-testid="ai-spent">
        {formatUsdMicros(spentMicros)}
      </span>{' '}
      {he.ai.of}{' '}
      <span className="ltr" data-testid="ai-limit">
        {formatUsdMicros(limitMicros)}
      </span>
    </>
  );
}

/**
 * This month's spend against a monthly limit: the amounts, a meter, and the state in words (a
 * colour alone says nothing to a screen reader, or in print).
 */
export function BudgetMeter({
  spentMicros,
  limitMicros,
  label,
  words,
}: {
  spentMicros: number;
  limitMicros: number;
  label: string;
  words: Readonly<Record<BudgetState, string>>;
}) {
  const state = budgetState(spentMicros, limitMicros);
  const percent = percentOf(spentMicros, limitMicros);
  return (
    <div className="flex flex-col gap-2 text-sm" data-testid="ai-budget" data-state={state}>
      <div className="flex flex-wrap items-center gap-2">
        <span>
          <SpentOfLimit spentMicros={spentMicros} limitMicros={limitMicros} />
        </span>
        <span className="ltr text-slate-500">{percent}%</span>
        <Badge tone={badgeTones[state]}>{he.ai.stateBadge[state]}</Badge>
      </div>
      <Meter role="meter" label={label} percent={percent} state={state} />
      <p className={state === 'OK' ? 'text-xs text-slate-500' : 'text-xs text-slate-700'}>
        {words[state]}
      </p>
    </div>
  );
}

/** "X מתוך Y מוכנים": the store's products that have copy of their own, with a progress bar. */
export function CopyProgress({
  products,
  label,
  testId,
}: {
  products: TenantAiStatus['products'];
  label: string;
  testId: string;
}) {
  return (
    <div className="flex flex-col gap-2 text-sm">
      <p data-testid={testId} data-with-copy={products.withCopy} data-total={products.total}>
        {products.withCopy} {he.ai.of} {products.total} {he.ai.card.ready}
      </p>
      <Meter
        role="progressbar"
        label={label}
        percent={percentOf(products.withCopy, products.total)}
      />
    </div>
  );
}

/** Whether a store's limit is the platform default or its own. */
export function LimitMark({ isDefault }: { isDefault: boolean }) {
  return (
    <span data-testid="ai-limit-kind" data-kind={isDefault ? 'default' : 'custom'}>
      <Badge tone={isDefault ? 'slate' : 'blue'}>
        {isDefault ? he.ai.limitDefault : he.ai.limitCustom}
      </Badge>
    </span>
  );
}
